import { Banknote, BarChart3, Building, CreditCard, MapPin, Settings, Users, Wallet } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useLocation } from 'react-router'

import { minimumRequiredRole } from '@ankor-io/common/domain/iam/account'
import { Role } from '@ankor-io/common/types/account'
import { AWXConnectedAccountStatus } from '@ankor-io/common/types/awx'

import AnkorLogo from '@/assets/AnkorLogoGradient.svg'
import { useConnectedAccount } from '@/providers/connectedAccount'
import { useUserContext } from '@/providers/user/useUserContext'
import { Link } from '@/ui-components/Link'
import { Navbar, NavbarDivider, NavbarSection, NavbarSpacer } from '@/ui-components/Navbar'
import {
  Sidebar,
  SidebarBody,
  SidebarFooter,
  SidebarHeader,
  SidebarItem,
  SidebarLabel,
  SidebarSection,
} from '@/ui-components/Sidebar'
import { SidebarLayout } from '@/ui-components/SidebarLayout'
import { ConnectedAccountsDropdown } from '@/views/components/ConnectedAccountsDropdown'
import OrganizationsModal from '@/views/components/OrganizationsModal/OrganizationsModal'
import UserAvatarMenu from '@/views/components/UserAvatarMenu'

export const Layout = ({ children }: { children: React.ReactNode }) => {
  const { organizations, activeOrganizationId, activeConnectedAccountId, activeOrganizationRole } = useUserContext()
  const location = useLocation()
  const { connectedAccounts } = useConnectedAccount()

  const [isOrganizationsModalOpen, setIsOrganizationsModalOpen] = useState(false)
  const activeOrganization = activeOrganizationId ? organizations[activeOrganizationId] || null : null

  const UNNAMED_ORGANIZATION = 'Unnamed Organization'

  type NavItem = {
    title: string
    url: string
    icon: React.ElementType
    alternativeUrls?: string[]
    disabled?: boolean
  }

  const hasNoConnectedAccounts = connectedAccounts.length === 0

  const isCurrentConnectedAccountInConnectedAccounts = connectedAccounts.some(
    (acct) => acct.id === activeConnectedAccountId,
  )

  const currentConnectedAccount = useMemo(
    () => connectedAccounts.find((acct) => acct.id === activeConnectedAccountId),
    [connectedAccounts, activeConnectedAccountId],
  )

  const isCurrentConnectedAccountActive = currentConnectedAccount?.status === AWXConnectedAccountStatus.ACTIVE

  const navItems: NavItem[] = [
    {
      title: 'Dashboard',
      url: '/',
      icon: BarChart3,
      disabled: hasNoConnectedAccounts || !isCurrentConnectedAccountInConnectedAccounts,
    },
    {
      title: 'Wallet',
      url: '/wallet',
      icon: Wallet,
      disabled:
        hasNoConnectedAccounts || !isCurrentConnectedAccountInConnectedAccounts || !isCurrentConnectedAccountActive,
    },
    {
      title: 'Charters',
      url: '/charters',
      icon: MapPin,
      disabled: hasNoConnectedAccounts || !isCurrentConnectedAccountInConnectedAccounts,
    },
    {
      title: 'Expenses',
      url: '/transactions',
      icon: Banknote,
      disabled:
        hasNoConnectedAccounts ||
        !isCurrentConnectedAccountInConnectedAccounts ||
        !minimumRequiredRole(activeOrganizationRole, Role.SPENDER),
    },
    {
      title: 'Cards',
      url: '/cards',
      icon: CreditCard,
      alternativeUrls: ['/my-cards', '/cards/cardholders'],
      disabled:
        hasNoConnectedAccounts || !isCurrentConnectedAccountInConnectedAccounts || !isCurrentConnectedAccountActive,
    },
    {
      title: 'Users',
      url: '/vessel-users',
      icon: Users,
      disabled: hasNoConnectedAccounts || !isCurrentConnectedAccountInConnectedAccounts,
    },
    {
      title: 'Account Settings',
      url: '/account-settings',
      icon: Settings,
      disabled: hasNoConnectedAccounts || !isCurrentConnectedAccountInConnectedAccounts,
    },
  ]

  const _Navbar = ({ className }: { className?: string }) => {
    return (
      <Navbar className={className}>
        <Link to="/">
          <img
            src={AnkorLogo}
            className="size-8"
          />
        </Link>
        <NavbarSpacer />
        <NavbarSection>
          <NavbarDivider />
          <UserAvatarMenu setIsOrganizationsModalOpen={setIsOrganizationsModalOpen} />
        </NavbarSection>
      </Navbar>
    )
  }

  const _Sidebar = () => (
    <Sidebar className="lg:pt-14 lg:border-r border-gray-200 dark:border-gray-600">
      <SidebarHeader>
        <ConnectedAccountsDropdown />
      </SidebarHeader>
      <SidebarBody className="p-2">
        <SidebarSection>
          {navItems.map((item) => {
            const isActive =
              location.pathname === item.url ||
              (item.url !== '/' && location.pathname.startsWith(item.url)) ||
              (item.url !== '/' && item.alternativeUrls?.some((url) => location.pathname.startsWith(url)))

            return (
              <SidebarItem
                key={item.title}
                to={item.url}
                current={isActive}
                aria-label={item.title}
                disabled={item.disabled}
              >
                <item.icon
                  data-slot="icon"
                  className="size-5 shrink-0"
                />
                <SidebarLabel>{item.title}</SidebarLabel>
              </SidebarItem>
            )
          })}
        </SidebarSection>
      </SidebarBody>

      <SidebarFooter className="p-2">
        <SidebarSection>
          <SidebarItem
            type="button"
            onClick={() => setIsOrganizationsModalOpen(true)}
            aria-label="Current Organization"
          >
            <Building
              data-slot="icon"
              className="size-4 shrink-0 text-primary-800 dark:text-primary-100"
            />
            <div className="ml-3 flex-1 min-w-0">
              <SidebarLabel>
                <span className="block text-xs text-primary-800 dark:text-primary-200/80 uppercase tracking-wider font-medium">
                  Organization
                </span>
                <span
                  data-testid="organization-name"
                  className="block text-sm text-gray-900 dark:text-white font-semibold truncate"
                >
                  {activeOrganization?.name || UNNAMED_ORGANIZATION}
                </span>
              </SidebarLabel>
            </div>
          </SidebarItem>
        </SidebarSection>
      </SidebarFooter>
    </Sidebar>
  )

  return (
    <>
      {/* Desktop navbar only */}
      <_Navbar className="hidden lg:flex fixed top-0 left-0 right-0 h-14 items-center px-4 shadow-sm z-30 gap-4 border-b border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800" />

      <SidebarLayout
        navbar={<_Navbar />}
        sidebar={<_Sidebar />}
      >
        {/* Main content with proper sidebar spacing */}
        <main className="flex-1 transition-all lg:py-8 duration-200 ease-in-out peer-data-[state=collapsed]:ml-[calc(var(--sidebar-width-icon))] peer-data-[state=expanded]:ml-[var(--sidebar-width)]">
          {children}
        </main>
      </SidebarLayout>

      <OrganizationsModal
        isOpen={isOrganizationsModalOpen}
        onClose={() => setIsOrganizationsModalOpen(false)}
      />
    </>
  )
}

export default Layout
